import { useRef, useState, useEffect, useCallback } from 'react';
import { throttle } from 'lodash';
import { zoomRoadmapWheelHandler } from './helpers';

const eventsFor = {
  touch: {
    start: 'touchstart',
    move: 'touchmove',
    stop: 'touchend',
  },
  mouse: {
    start: 'mousedown',
    move: 'mousemove',
    stop: 'mouseup',
  },
};

export const useDraggable = ({ onStartDrag, onDragMove }) => {
  const activeEvent = useRef<{ move: string; stop: string }>();
  const positionX = useRef<number>();
  const [isDragging, setIsDragging] = useState(false);

  const onDrag = event => {
    event.preventDefault();
    onDragMove(event.clientX - positionX.current);
  };

  const onDragStop = () => {
    if (activeEvent.current) {
      document.removeEventListener(activeEvent.current.move, onDrag);
      document.removeEventListener(activeEvent.current.stop, onDragStop);
    }
    activeEvent.current = null;
    positionX.current = null;
    setIsDragging(false);
  };

  const onDragStart = (event, dragEvents) => {
    // 0 is a left click
    if (event.button !== 0) return;
    event.stopPropagation();
    onStartDrag();
    activeEvent.current = dragEvents;
    positionX.current = event.clientX;
    document.addEventListener(dragEvents.move, onDrag);
    document.addEventListener(dragEvents.stop, onDragStop);
    setIsDragging(true);
  };

  const dragProps = {
    onMouseDown: event => onDragStart(event, eventsFor.mouse),
    onTouchStart: event => onDragStart(event, eventsFor.touch),
  };

  return { dragProps, isDragging };
};

// We throttle the scroll wheel events since a lot of them are fired,
// and can cause slower redraws
const throttledZoomRoadmap = throttle(zoomRoadmapWheelHandler, 50);

export interface UseScrollZoomProps {
  startDate: string;
  pixelsPerDay: number;
  endDate: string;
  hideWeekends: boolean;
  setVisibleDates: (startDate: string, endDate: string) => void;
  visibleStartDate: string;
  visibleEndDate: string;
  sidebarWidth?: number;
  scrollDistancePercent?: number;
}

export const useScrollZoom = ({
  startDate,
  pixelsPerDay,
  endDate,
  hideWeekends,
  setVisibleDates,
  visibleStartDate,
  visibleEndDate,
  sidebarWidth,
  scrollDistancePercent,
}: UseScrollZoomProps) => {
  const scrollZoomRef = useRef(null);
  const calculateZoom = useCallback(
    e =>
      throttledZoomRoadmap(e, scrollZoomRef, {
        endDate,
        hideWeekends,
        pixelsPerDay,
        scrollDistancePercent,
        setVisibleDates,
        startDate,
        visibleEndDate,
        visibleStartDate,
        sidebarWidth,
      }),
    [
      endDate,
      hideWeekends,
      pixelsPerDay,
      scrollDistancePercent,
      setVisibleDates,
      startDate,
      sidebarWidth,
      visibleEndDate,
      visibleStartDate,
    ]
  );

  useEffect(() => {
    const domRef = scrollZoomRef.current;
    const handleZoom = e => {
      if (!e.ctrlKey) return;

      // If we don't prevent propagation, this will causes
      // chrome to zoom.
      e.preventDefault();
      e.stopPropagation();

      calculateZoom(e);
    };

    domRef.addEventListener('wheel', handleZoom, { passive: false });

    return () => {
      domRef.removeEventListener('wheel', handleZoom, { passive: false });
    };
  }, [calculateZoom]);

  return scrollZoomRef;
};
